import React from "react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/shared/components/ui/use-toast";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import {
  Copy, Check, ArrowDownToLine, ArrowUpFromLine, Clock, CheckCircle2,
  XCircle, Loader2, Zap, Users, Building2, Phone, RefreshCw, Edit,
} from "lucide-react";
import DrillDownBreadcrumb from "./DrillDownBreadcrumb";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import * as transactionService from "@/shared/api/services/transactionService";

/* ── Design Tokens ── */
const C = {
  bg: "#060C18", surface: "#0A1628", card: "#0E1C35", cardHigh: "#122040",
  border: "rgba(255,255,255,0.055)", borderMid: "rgba(255,255,255,0.09)",
  cyan: "#22D3EE", cyanSoft: "rgba(34,211,238,0.08)", cyanGlow: "rgba(34,211,238,0.18)",
  blue: "#3B82F6", blueSoft: "rgba(59,130,246,0.10)",
  emerald: "#10B981", emeraldSoft: "rgba(16,185,129,0.10)",
  amber: "#FBBF24", amberSoft: "rgba(251,191,36,0.08)",
  rose: "#FB7185", roseSoft: "rgba(251,113,133,0.10)",
  purple: "#A78BFA",
  t1: "#EFF6FF", t2: "#93B4D4", t3: "#4D6E90",
};

const statusConfigDefault = {
  pending: { icon: Clock, bg: "rgba(251,191,36,0.10)", border: "rgba(251,191,36,0.25)", text: "#FBBF24", amountColor: "#22D3EE" },
  processing: { icon: Loader2, bg: "rgba(59,130,246,0.10)", border: "rgba(59,130,246,0.25)", text: "#60A5FA", amountColor: "#22D3EE" },
  success: { icon: CheckCircle2, bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.25)", text: "#34D399", amountColor: "#34D399" },
  failed: { icon: XCircle, bg: "rgba(251,113,133,0.10)", border: "rgba(251,113,133,0.25)", text: "#FB7185", amountColor: "#FB7185" },
  expired: { icon: Clock, bg: "rgba(71,85,105,0.15)", border: "rgba(71,85,105,0.28)", text: "#64748B", amountColor: "#64748B" },
  cancelled: { icon: XCircle, bg: "rgba(71,85,105,0.15)", border: "rgba(71,85,105,0.28)", text: "#64748B", amountColor: "#64748B" },
  declined: { icon: XCircle, bg: "rgba(251,113,133,0.10)", border: "rgba(251,113,133,0.25)", text: "#FB7185", amountColor: "#FB7185" },
};

const getStatusConfig = (t) => {
  const labels = {
    pending: t?.("pending") || "Pending", processing: t?.("processing") || "Processing",
    success: t?.("success") || "Success", failed: t?.("failed") || "Failed",
    expired: t?.("expired") || "Expired", cancelled: t?.("cancelled") || "Cancelled",
    declined: t?.("declined") || "Declined",
  };
  return Object.fromEntries(Object.entries(statusConfigDefault).map(([k, v]) => [k, { ...v, label: labels[k] }]));
};

/* ── Scoped CSS ── */
const TDP_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');

  .tdp-sheet [data-radix-scroll-area-viewport],
  .tdp-sheet { background: #060C18 !important; border-left: 1px solid rgba(255,255,255,0.07) !important; }
  [role="dialog"].tdp-sheet, .tdp-sheet[data-state] { background: #060C18 !important; }
  .tdp-sheet * { font-family: 'Outfit', system-ui, sans-serif; }

  .tdp-sheet ::-webkit-scrollbar { width: 5px; }
  .tdp-sheet ::-webkit-scrollbar-track { background: transparent; }
  .tdp-sheet ::-webkit-scrollbar-thumb { background: rgba(34,211,238,0.2); border-radius: 99px; }

  /* Select override */
  .tdp-sheet button[role="combobox"],
  .tdp-sheet [data-radix-select-trigger] {
    background: #0E1C35 !important; border: 1px solid rgba(255,255,255,0.08) !important;
    color: #93B4D4 !important; border-radius: 10px !important; height: 38px !important;
    font-size: 13px !important; transition: all .2s !important;
  }
  .tdp-sheet button[role="combobox"]:hover { border-color: rgba(34,211,238,0.3) !important; color: #EFF6FF !important; }

  /* Section title */
  .tdp-section-title {
    font-size: 0.68rem; font-weight: 800; color: #4D6E90;
    text-transform: uppercase; letter-spacing: 0.1em;
    margin: 1.25rem 0 0.5rem;
    display: flex; align-items: center; gap: 0.5rem;
  }
  .tdp-section-title::after { content: ''; flex: 1; height: 1px; background: rgba(255,255,255,0.055); }

  /* Info card */
  .tdp-info-card {
    background: #0E1C35; border: 1px solid rgba(255,255,255,0.055);
    border-radius: 0.75rem; overflow: hidden; margin-bottom: 0.75rem;
    transition: border-color 0.2s;
  }
  .tdp-info-card:hover { border-color: rgba(34,211,238,0.2); }

  /* Info row */
  .tdp-info-row {
    display: flex; align-items: flex-start; justify-content: space-between;
    padding: 0.6rem 0.875rem; border-bottom: 1px solid rgba(255,255,255,0.04);
    transition: background 0.15s;
  }
  .tdp-info-row:last-child { border-bottom: none; }
  .tdp-info-row:hover { background: rgba(34,211,238,0.03); }
  .tdp-row-label { font-size: 0.74rem; color: #4D6E90; font-weight: 500; padding-right: 1rem; flex-shrink: 0; white-space: nowrap; }
  .tdp-row-value { font-size: 0.78rem; color: #93B4D4; text-align: right; word-break: break-all; }
  .tdp-row-value.mono { font-family: ui-monospace, monospace; color: #22D3EE; }

  /* Quick link card */
  .tdp-quick-link {
    padding: 0.625rem 0.5rem; border-radius: 0.625rem;
    border: 1px solid rgba(255,255,255,0.055); background: #0E1C35;
    display: flex; flex-direction: column; align-items: center; gap: 0.3rem;
    cursor: pointer; transition: all 0.2s; text-decoration: none;
  }
  .tdp-quick-link:hover { border-color: rgba(34,211,238,0.3); background: rgba(34,211,238,0.06); }
  .tdp-quick-link span { font-size: 0.7rem; font-weight: 600; color: #93B4D4; }

  /* Timeline */
  .tdp-timeline-dot {
    width: 2rem; height: 2rem; border-radius: 9999px;
    display: flex; align-items: center; justify-content: center;
  }
  .tdp-timeline-line { width: 1px; flex: 1; min-height: 1.5rem; }

  /* Copy button */
  .tdp-copy-btn {
    width: 2.25rem; height: 2.25rem; border-radius: 0.5rem;
    border: 1px solid rgba(255,255,255,0.055); background: #0E1C35;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.2s; flex-shrink: 0;
  }
  .tdp-copy-btn:hover { border-color: rgba(34,211,238,0.38); background: rgba(34,211,238,0.08); }

  /* Status badge */
  .tdp-status-badge {
    display: inline-flex; align-items: center; gap: 0.3rem;
    padding: 0.2rem 0.5rem; border-radius: 9999px;
    font-size: 0.68rem; font-weight: 700;
  }

  /* Update button */
  .tdp-update-btn {
    padding: 0.3rem 0.75rem; border-radius: 0.5rem;
    border: 1px solid rgba(59,130,246,0.28); background: rgba(59,130,246,0.10);
    color: #60A5FA; font-size: 0.78rem; font-weight: 600; cursor: pointer;
    transition: all 0.2s;
  }
  .tdp-update-btn:hover { background: rgba(59,130,246,0.18); }

  .tdp-submit-btn {
    width: 100%; padding: 0.55rem 1rem; border-radius: 0.625rem;
    border: 1px solid rgba(34,211,238,0.3); background: rgba(34,211,238,0.08);
    color: #22D3EE; font-size: 0.8375rem; font-weight: 700; cursor: pointer;
    transition: all 0.2s;
  }
  .tdp-submit-btn:hover:not(:disabled) { background: rgba(34,211,238,0.15); box-shadow: 0 0 14px rgba(34,211,238,0.2); }
  .tdp-submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .tdp-textarea {
    width: 100%; min-height: 80px; padding: 0.625rem 0.75rem; resize: vertical;
    background: #0E1C35; border: 1px solid rgba(255,255,255,0.08); border-radius: 0.625rem;
    color: #EFF6FF; font-size: 0.8rem; font-family: 'Outfit', system-ui, sans-serif;
    outline: none; transition: border-color 0.2s;
  }
  .tdp-textarea:focus { border-color: rgba(34,211,238,0.4); box-shadow: 0 0 0 2px rgba(34,211,238,0.1); }
  .tdp-textarea::placeholder { color: #4D6E90; }
`;

export default function TransactionDetailPanel({
  transaction, open, onClose, engine, reseller, merchant, isQRCode = false,
}) {
  const [copied, setCopied] = React.useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = React.useState(false);
  const [newStatus, setNewStatus] = React.useState(transaction?.status?.toUpperCase() || "PENDING");
  const [adminNote, setAdminNote] = React.useState("");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  let t = null;
  try { const langContext = useLanguage(); t = langContext?.t; } catch (e) { }

  const statusConfig = getStatusConfig(t);

  const getTransactionTypeLabel = (type) => {
    if (!t) return type;
    const typeUpper = (type || "").toUpperCase();
    if (typeUpper === "DEPOSIT") return t("deposit");
    if (typeUpper === "WITHDRAWAL") return t("withdrawal");
    return type;
  };

  React.useEffect(() => {
    if (transaction) {
      setNewStatus(transaction.status?.toUpperCase() || "PENDING");
      setAdminNote("");
      setShowStatusUpdate(false);
    }
  }, [transaction?.id]);

  const updateStatusMutation = useMutation({
    mutationFn: async () => {
      return transactionService.updateTransactionStatus(transaction.id, newStatus, adminNote || undefined);
    },
    onSuccess: (updatedTransaction) => {
      queryClient.invalidateQueries(["all-transactions"]);
      queryClient.invalidateQueries(["transactions"]);
      queryClient.invalidateQueries(["transactionById", transaction.id]);
      if (transaction) Object.assign(transaction, updatedTransaction);
      toast({
        title: t ? t("statusUpdated") : "Status updated",
        description: t
          ? `${t("transactionStatusChangedTo")} ${t(newStatus.toLowerCase()) || newStatus}`
          : `Transaction status changed to ${newStatus}`,
      });
      setShowStatusUpdate(false);
      setAdminNote("");
    },
    onError: (error) => {
      toast({
        title: t ? t("updateFailed") : "Update failed",
        description: error.message || (t ? t("failedToUpdateTransactionStatus") : "Failed to update transaction status"),
        variant: "destructive",
      });
    },
  });

  const copyId = () => {
    const idToCopy = isQRCode ? transaction?.payment_id : transaction?.transaction_id;
    navigator.clipboard.writeText(idToCopy || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!transaction) return null;

  const statusKey = (transaction?.status || "").toLowerCase();
  const status = statusConfig[statusKey] || statusConfig.pending;
  const StatusIcon = status.icon;
  const isWithdrawal = (transaction.type || "").toLowerCase() === "withdrawal";

  const statusTimeline = [
    {
      status: "created",
      label: t ? (t("createdLabel") || t("created")) : "Created",
      time: transaction.created_date || transaction.createdAt,
      done: true,
    },
    {
      status: "processing",
      label: t ? t("processing") : "Processing",
      time: transaction.processed_at || transaction.processedAt,
      done: ["processing", "success", "failed"].includes(transaction.status),
    },
    {
      status: transaction.status === "failed" ? "failed" : "success",
      label: transaction.status === "failed" ? (t ? t("failed") : "Failed") : (t ? t("completed") : "Completed"),
      time: transaction.completed_at || transaction.completedAt,
      done: ["success", "failed"].includes(transaction.status),
    },
  ];

  /* Sub-components */
  const SectionTitle = ({ label }) => (
    <p className="tdp-section-title">{label}</p>
  );

  const InfoCard = ({ children }) => (
    <div className="tdp-info-card">{children}</div>
  );

  const InfoRow = ({ label, value, mono }) => (
    <div className="tdp-info-row">
      <span className="tdp-row-label">{label}</span>
      <span className={`tdp-row-value ${mono ? "mono" : ""}`}>{value || "N/A"}</span>
    </div>
  );

  const QuickLinkCard = ({ icon: Icon, label, color, to }) => (
    <Link to={to} className="tdp-quick-link">
      <Icon style={{ width: "1rem", height: "1rem", color }} />
      <span>{label}</span>
    </Link>
  );

  return (
    <>
      <style>{TDP_CSS}</style>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent className="tdp-sheet w-full sm:max-w-xl overflow-y-auto" style={{ background: C.bg, border: "none" }}>

          {/* Header */}
          <SheetHeader style={{ padding: "1.25rem 1.25rem 0.875rem", borderBottom: `1px solid ${C.border}`, background: "rgba(34,211,238,0.02)" }}>
            <SheetTitle style={{ display: "flex", alignItems: "center", gap: "0.875rem" }}>
              <div style={{
                width: "2.75rem", height: "2.75rem", borderRadius: "0.875rem",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                background: isWithdrawal ? "rgba(251,191,36,0.10)" : "rgba(59,130,246,0.10)",
                border: `1px solid ${isWithdrawal ? "rgba(251,191,36,0.25)" : "rgba(59,130,246,0.25)"}`,
              }}>
                {isWithdrawal
                  ? <ArrowUpFromLine style={{ width: "1.2rem", height: "1.2rem", color: C.amber }} />
                  : <ArrowDownToLine style={{ width: "1.2rem", height: "1.2rem", color: C.blue }} />}
              </div>
              <div>
                <p style={{ fontSize: "1rem", fontWeight: 700, color: C.t1, margin: 0 }}>
                  {isQRCode ? (t ? t("qrCodePayment") : "QR Code Payment") : (getTransactionTypeLabel(transaction.type) || (t ? t("transaction") : "Transaction"))}
                </p>
                <div style={{ marginTop: "0.25rem" }}>
                  <span className="tdp-status-badge" style={{ background: status.bg, border: `1px solid ${status.border}`, color: status.text }}>
                    <StatusIcon style={{ width: "0.65rem", height: "0.65rem" }} />
                    {status.label}
                  </span>
                </div>
              </div>
            </SheetTitle>
          </SheetHeader>

          <div style={{ background: C.bg, padding: "0 1.25rem 2rem", paddingTop: "1.25rem" }}>

            {/* Drill-Down */}
            <DrillDownBreadcrumb engine={engine} reseller={reseller} merchant={merchant} transaction={transaction} />

            {/* Amount banner */}
            <div style={{
              borderRadius: "0.875rem", padding: "1rem 1.25rem", marginBottom: "1.25rem", textAlign: "center",
              background: "rgba(34,211,238,0.05)", border: "1px solid rgba(34,211,238,0.15)",
            }}>
              <p style={{ fontSize: "0.75rem", color: C.t3, marginBottom: "0.25rem" }}>{t ? t("netAmount") || "Net Amount" : "Net Amount"}</p>
              <p style={{ fontSize: "2rem", fontWeight: 800, color: status.amountColor, letterSpacing: "-0.03em", margin: 0 }}>
                {isWithdrawal ? "−" : "+"}฿{(transaction.net_amount ?? transaction.amount ?? 0).toLocaleString()}
              </p>
            </div>

            {/* ID display */}
            <div style={{ marginBottom: "1.25rem" }}>
              <p style={{ fontSize: "0.74rem", color: C.t3, marginBottom: "0.375rem", fontWeight: 600 }}>
                {isQRCode ? (t ? t("paymentId") || "Payment ID" : "Payment ID") : (t ? t("transactionId") : "Transaction ID")}
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <code style={{
                  flex: 1, background: C.cardHigh, border: "1px solid rgba(34,211,238,0.18)",
                  borderRadius: "0.625rem", padding: "0.5rem 0.75rem",
                  fontFamily: "ui-monospace, monospace", fontSize: "0.78rem", color: C.cyan,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {isQRCode ? transaction.transaction_id : transaction.transaction_id}
                </code>
                <button className="tdp-copy-btn" onClick={copyId}>
                  {copied
                    ? <Check style={{ width: "0.9rem", height: "0.9rem", color: C.emerald }} />
                    : <Copy style={{ width: "0.9rem", height: "0.9rem", color: C.t3 }} />}
                </button>
              </div>
            </div>

            {/* Reference (QR only) */}
            {isQRCode && transaction.reference && (
              <div style={{ marginBottom: "1.25rem" }}>
                <p style={{ fontSize: "0.74rem", color: C.t3, marginBottom: "0.375rem", fontWeight: 600 }}>{t ? t("reference") : "Reference"}</p>
                <code style={{
                  display: "block", background: C.cardHigh, border: `1px solid ${C.border}`,
                  borderRadius: "0.625rem", padding: "0.5rem 0.75rem",
                  fontFamily: "ui-monospace, monospace", fontSize: "0.78rem", color: C.cyan,
                }}>
                  {transaction.reference}
                </code>
              </div>
            )}

            {/* Quick links */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.5rem", marginBottom: "1.25rem" }}>
              {isQRCode && transaction?.reseller?.id && (
                <QuickLinkCard icon={Users} label={t ? (t("reseller") || t("resellers")) : "Reseller"} color={C.purple} to={createPageUrl("AdminResellerDetail") + `?id=${transaction.reseller.id}`} />
              )}
              {!isQRCode && engine?.id && (
                <QuickLinkCard icon={Zap} label={t ? (t("engine") || t("engines")) : "Engine"} color={C.cyan} to={createPageUrl("AdminEngineDetail") + `?id=${engine.id}`} />
              )}
              {!isQRCode && reseller?.id && (
                <QuickLinkCard icon={Users} label={t ? (t("reseller") || t("resellers")) : "Reseller"} color={C.purple} to={createPageUrl("AdminResellerDetail") + `?id=${reseller.id}`} />
              )}
              {(merchant?.id || transaction?.merchant_id) && (
                <QuickLinkCard icon={Building2} label={t ? t("merchant") : "Merchant"} color={C.blue} to={createPageUrl("AdminMerchantDetail") + `?id=${merchant?.id || transaction.merchant_id}`} />
              )}
            </div>

            {/* Details */}
            <SectionTitle label={t ? t("details") || "Details" : "Details"} />
            <InfoCard>
              {isQRCode ? (
                <>
                  <InfoRow label={t ? t("paymentId") || "Payment ID" : "Payment ID"} value={transaction.transaction_id || transaction.payment_id} mono />
                  <InfoRow label={t ? t("provider") : "Provider"} value={transaction.client_bank_name} />
                  {transaction.client_name && <InfoRow label={t ? t("clientName") || "Client Name" : "Client Name"} value={transaction.client_name} />}
                  {transaction.client_bank_account && <InfoRow label={t ? t("clientBankAccount") || "Client Bank Account" : "Client Bank Account"} value={transaction.client_bank_account} mono />}
                </>
              ) : (
                <>
                  <InfoRow label={t ? t("provider") : "Provider"} value={transaction.provider_display || (t ? t("nA") : "N/A")} />
                  <InfoRow label={t ? t("merchantRef") : "Merchant Ref"} value={transaction.merchant_ref} mono />
                </>
              )}
              <InfoRow label={t ? t("grossAmount") || "Gross Amount" : "Gross Amount"} value={`฿${(transaction.amount ?? 0).toLocaleString()}`} />
              <InfoRow label={t ? t("totalFee") || "Total Fee" : "Total Fee"} value={`฿${(transaction.total_fee ?? transaction.fee_amount ?? 0).toLocaleString()}`} />
              <InfoRow label={t ? t("netAmount") || "Net Amount" : "Net Amount"} value={`฿${(transaction.net_amount ?? transaction.amount ?? 0).toLocaleString()}`} />
              <InfoRow label={t ? t("currency") || "Currency" : "Currency"} value={transaction.currency || "THB"} />
            </InfoCard>

            {/* Customer */}
            {!isQRCode && (transaction.customer_name || transaction.customer_email || transaction.customer_phone) && (
              <>
                <SectionTitle label={t ? t("customerInfo") : "Customer Info"} />
                <InfoCard>
                  {transaction.customer_name && <InfoRow label={t ? t("name") : "Name"} value={transaction.customer_name} />}
                  {transaction.customer_email && <InfoRow label={t ? t("email") : "Email"} value={transaction.customer_email} />}
                  {transaction.customer_phone && <InfoRow label={t ? t("phone") : "Phone"} value={transaction.customer_phone} />}
                </InfoCard>
              </>
            )}

            {/* Bank */}
            {!isQRCode && isWithdrawal && (transaction.bank_code || transaction.bank_account) && (
              <>
                <SectionTitle label={t ? t("bankDetails") : "Bank Details"} />
                <InfoCard>
                  <InfoRow label={t ? t("bank") : "Bank"} value={transaction.bank_code} />
                  <InfoRow label={t ? t("account") : "Account"} value={transaction.bank_account} mono />
                </InfoCard>
              </>
            )}

            {/* QR Expiry */}
            {isQRCode && transaction.expires_at && (
              <>
                <SectionTitle label={t ? t("qrPaymentInfo") : "QR Payment Info"} />
                <InfoCard>
                  <InfoRow label={t ? t("expires") : "Expires"} value={format(new Date(transaction.expires_at), "MMM d, yyyy HH:mm")} />
                </InfoCard>
              </>
            )}

            {/* Manual Status Update */}
            {!isQRCode && (
              <>
                <SectionTitle label={t ? t("manualStatusUpdate") : "Manual Status Update"} />
                <div style={{
                  background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.18)",
                  borderRadius: "0.875rem", padding: "1rem", marginBottom: "0.75rem",
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
                    <p style={{ fontSize: "0.8rem", fontWeight: 700, color: "#60A5FA", display: "flex", alignItems: "center", gap: "0.4rem", margin: 0 }}>
                      <Edit style={{ width: "0.8rem", height: "0.8rem" }} />
                      {t ? t("manualStatusUpdate") : "Manual Status Update"}
                    </p>
                    <button className="tdp-update-btn" onClick={() => setShowStatusUpdate(!showStatusUpdate)}>
                      {showStatusUpdate ? (t ? t("cancel") : "Cancel") : (t ? t("updateStatus") : "Update Status")}
                    </button>
                  </div>

                  {showStatusUpdate && (
                    <div style={{ borderTop: "1px solid rgba(59,130,246,0.15)", paddingTop: "0.875rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                      <div>
                        <label style={{ fontSize: "0.74rem", fontWeight: 600, color: "#60A5FA", display: "block", marginBottom: "0.375rem" }}>
                          {t ? t("newStatus") : "New Status"}
                        </label>
                        <Select value={newStatus} onValueChange={setNewStatus}>
                          <SelectTrigger style={{ background: C.card, width: "100%" }}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent style={{ background: "#0a1628", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10 }}>
                            {["PENDING", "PROCESSING", "SUCCESS", "FAILED", "EXPIRED", "CANCELLED"].map(s => (
                              <SelectItem key={s} value={s} style={{ color: "#eff6ff" }}>
                                {t ? t(s.toLowerCase()) : s}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label style={{ fontSize: "0.74rem", fontWeight: 600, color: "#60A5FA", display: "block", marginBottom: "0.375rem" }}>
                          {t ? t("adminNoteOptional") : "Admin Note (Optional)"}
                        </label>
                        <textarea
                          className="tdp-textarea"
                          placeholder={t ? t("reasonForStatusChange") : "Reason for status change..."}
                          value={adminNote}
                          onChange={(e) => setAdminNote(e.target.value)}
                          maxLength={500}
                        />
                        <p style={{ fontSize: "0.7rem", color: "#60A5FA", marginTop: "0.25rem" }}>
                          {adminNote.length}/500 {t ? t("characters") : "characters"}
                        </p>
                      </div>
                      <button
                        className="tdp-submit-btn"
                        onClick={() => updateStatusMutation.mutate()}
                        disabled={updateStatusMutation.isPending || newStatus?.toUpperCase() === transaction.status?.toUpperCase()}
                      >
                        {updateStatusMutation.isPending ? (t ? t("updating") : "Updating...") : (t ? t("updateStatus") : "Update Status")}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Admin note */}
            {!isQRCode && transaction.admin_note && (
              <>
                <SectionTitle label={t ? t("adminNote") : "Admin Note"} />
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: "0.75rem", padding: "0.875rem", marginBottom: "0.75rem" }}>
                  <p style={{ fontSize: "0.8rem", color: C.t2, margin: 0 }}>{transaction.admin_note}</p>
                </div>
              </>
            )}

            {/* Status Timeline */}
            {!isQRCode && (
              <>
                <SectionTitle label={t ? t("statusTimeline") : "Status Timeline"} />
                <div style={{ display: "flex", flexDirection: "column", gap: "0.875rem", marginBottom: "0.75rem" }}>
                  {statusTimeline.map((step, index) => {
                    const isLast = index === statusTimeline.length - 1;
                    const isFailed = step.status === "failed";
                    return (
                      <div key={step.status} style={{ display: "flex", gap: "0.75rem" }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                          <div className="tdp-timeline-dot" style={{
                            background: step.done ? (isFailed ? "rgba(251,113,133,0.12)" : "rgba(16,185,129,0.12)") : "rgba(255,255,255,0.04)",
                            border: `1px solid ${step.done ? (isFailed ? "rgba(251,113,133,0.28)" : "rgba(16,185,129,0.28)") : C.border}`,
                          }}>
                            {step.done
                              ? (isFailed
                                ? <XCircle style={{ width: "0.875rem", height: "0.875rem", color: C.rose }} />
                                : <CheckCircle2 style={{ width: "0.875rem", height: "0.875rem", color: C.emerald }} />)
                              : <Clock style={{ width: "0.875rem", height: "0.875rem", color: C.t3 }} />}
                          </div>
                          {!isLast && <div className="tdp-timeline-line" style={{ background: step.done ? "rgba(16,185,129,0.2)" : C.border }} />}
                        </div>
                        <div style={{ flex: 1, paddingBottom: "0.5rem" }}>
                          <p style={{ fontWeight: 600, fontSize: "0.8375rem", color: step.done ? C.t1 : C.t3, margin: "0 0 0.125rem" }}>{step.label}</p>
                          {step.time && step.done && (
                            <p style={{ fontSize: "0.72rem", color: C.t3, margin: 0, fontFamily: "ui-monospace, monospace" }}>
                              {format(new Date(step.time), "MMM d, yyyy HH:mm:ss")}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Callback */}
            {!isQRCode && (
              <>
                <SectionTitle label={t ? t("callbackStatus") : "Callback Status"} />
                <InfoCard>
                  <div style={{ padding: "0.75rem 0.875rem", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${C.border}` }}>
                    <span style={{ fontSize: "0.74rem", color: C.t3, display: "flex", alignItems: "center", gap: "0.35rem" }}>
                      <Phone style={{ width: "0.75rem", height: "0.75rem" }} />
                      {t ? t("callbackStatus") : "Callback Status"}
                    </span>
                    {(() => {
                      const st = (transaction.status || "pending").toLowerCase();
                      const isSuccess = st === "success" || st === "delivered";
                      const color = isSuccess ? C.emerald : st === "failed" ? C.rose : C.amber;
                      const bg = isSuccess ? C.emeraldSoft : st === "failed" ? C.roseSoft : C.amberSoft;
                      const label = isSuccess ? (t ? t("delivered") || "Delivered" : "Delivered") : st === "failed" ? (t ? t("failed") : "Failed") : (t ? t("pending") : "Pending");
                      return <span style={{ padding: "0.18rem 0.5rem", borderRadius: "9999px", fontSize: "0.68rem", fontWeight: 700, background: bg, color }}>{label}</span>;
                    })()}
                  </div>
                  <InfoRow label={t ? t("url") : "URL"} value={transaction.callback_url} mono />
                  <InfoRow label={t ? t("attempts") : "Attempts"} value={transaction.callback_attempts || 0} />
                  {transaction.callback_last_attempt && (
                    <InfoRow label={t ? t("lastAttempt") : "Last Attempt"} value={format(new Date(transaction.callback_last_attempt), "MMM d, HH:mm:ss")} />
                  )}
                </InfoCard>
              </>
            )}

            {/* Webhook */}
            {!isQRCode && (
              <>
                <SectionTitle label={t ? t("webhookInfo") : "Webhook Info"} />
                <InfoCard>
                  {transaction.webhook_received_at && (
                    <InfoRow label={t ? t("received") : "Received"} value={format(new Date(transaction.webhook_received_at), "MMM d, HH:mm:ss")} />
                  )}
                  <InfoRow label={t ? t("providerRef") : "Provider Ref"} value={transaction.provider_ref} mono />
                  {transaction.webhook_data && (
                    <div style={{ padding: "0.75rem 0.875rem" }}>
                      <p style={{ fontSize: "0.7rem", color: C.t3, marginBottom: "0.375rem" }}>{t ? t("rawWebhookData") : "Raw Webhook Data"}</p>
                      <pre style={{
                        background: C.surface, color: C.t2, padding: "0.625rem 0.75rem",
                        borderRadius: "0.5rem", fontSize: "0.72rem", overflowX: "auto",
                        maxHeight: "8rem", border: `1px solid ${C.border}`, margin: 0,
                      }}>
                        {transaction.webhook_data}
                      </pre>
                    </div>
                  )}
                </InfoCard>
              </>
            )}

            {/* Routing Attempts */}
            {!isQRCode && transaction.routing_attempts > 1 && (
              <div style={{ background: C.amberSoft, border: "1px solid rgba(251,191,36,0.22)", borderRadius: "0.875rem", padding: "0.875rem", marginBottom: "0.75rem" }}>
                <p style={{ fontSize: "0.8rem", fontWeight: 600, color: C.amber, display: "flex", alignItems: "center", gap: "0.375rem", margin: "0 0 0.375rem" }}>
                  <RefreshCw style={{ width: "0.8rem", height: "0.8rem" }} />
                  {t ? t("routingAttempts") : "Routing Attempts"}: {transaction.routing_attempts}
                </p>
                {transaction.routing_history && (
                  <pre style={{ fontSize: "0.72rem", color: C.amber, overflow: "auto", margin: 0 }}>{transaction.routing_history}</pre>
                )}
              </div>
            )}

            {/* Error Message */}
            {!isQRCode && transaction.error_message && (
              <div style={{ background: C.roseSoft, border: "1px solid rgba(251,113,133,0.22)", borderRadius: "0.875rem", padding: "0.875rem", marginBottom: "0.75rem" }}>
                <p style={{ fontSize: "0.8rem", fontWeight: 700, color: C.rose, margin: "0 0 0.25rem" }}>{t ? t("error") : "Error"}</p>
                <p style={{ fontSize: "0.8rem", color: "#FCA5A5", margin: 0 }}>{transaction.error_message}</p>
                {transaction.error_code && (
                  <p style={{ fontSize: "0.72rem", color: "#FB7185", marginTop: "0.25rem" }}>
                    {(t && t("errorCode")) || "Error Code"}: {transaction.error_code}
                  </p>
                )}
              </div>
            )}

            {/* Timestamps */}
            <SectionTitle label={t ? t("timestamps") || "Timestamps" : "Timestamps"} />
            <InfoCard>
              <InfoRow
                label={t ? (t("created") || t("createdAt")) : "Created"}
                value={
                  (transaction.created_date || transaction.createdAt)
                    ? format(new Date(transaction.created_date || transaction.createdAt), "MMM d, yyyy HH:mm:ss")
                    : "N/A"
                }
              />
              <InfoRow
                label={t ? t("updated") : "Updated"}
                value={
                  (transaction.updatedAt || transaction.completed_at || transaction.completedAt)
                    ? format(new Date(transaction.updatedAt || transaction.completed_at || transaction.completedAt), "MMM d, yyyy HH:mm:ss")
                    : "N/A"
                }
              />
              {!isQRCode && transaction.ip_address && (
                <InfoRow label={t ? t("ip") : "IP"} value={transaction.ip_address} mono />
              )}
            </InfoCard>

          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}